/**
 * Amount entry for a plain text input (inputmode="decimal"). The draft is the canonical typed
 * string ('', '250', '12.5', '0.'): digits, at most one '.', at most two decimals, at most
 * MAX_WHOLE_DIGITS whole digits. The input shows it grouped ('1,250.5') while typing.
 */

export const MAX_WHOLE_DIGITS = 8;

/** Characters that carry meaning in a draft (digits and the decimal point). */
const isSignificant = (ch: string) => (ch >= '0' && ch <= '9') || ch === '.';

/**
 * Turns whatever is in the input (grouping commas, a ₱ sign, spaces, pasted text) into a draft.
 * Commas are always grouping; a decimal comma typed on a comma-decimal keyboard is turned into '.'
 * by the caller before this runs (it knows which character was just typed).
 */
export function parseAmountInput(raw: string): string {
  let whole = '';
  let decimals = '';
  let seenDot = false;
  for (const ch of raw) {
    if (ch === '.') {
      seenDot = true;
      continue;
    }
    if (ch < '0' || ch > '9') continue;
    if (seenDot) {
      if (decimals.length < 2) decimals += ch;
    } else if (whole === '0') {
      whole = ch; // a lone leading zero is replaced
    } else if (whole.length < MAX_WHOLE_DIGITS) {
      whole += ch;
    }
  }
  if (!seenDot) return whole;
  return `${whole || '0'}.${decimals}`;
}

/**
 * Pasted text in another notation: "1.250,50" or "12,5" use a decimal comma. Only used for paste,
 * where there's no keystroke to go by. A comma followed by exactly three digits is grouping.
 */
export function parsePastedAmount(raw: string): string {
  const text = raw.replace(/[^\d.,]/g, '');
  const lastComma = text.lastIndexOf(',');
  const lastDot = text.lastIndexOf('.');
  if (lastComma > lastDot) {
    const tail = text.slice(lastComma + 1);
    if (tail.length !== 3) {
      // Decimal comma: drop the dots (grouping), make the last comma the point.
      return parseAmountInput(
        `${text.slice(0, lastComma).replace(/[.,]/g, '')}.${tail.replace(/,/g, '')}`,
      );
    }
  }
  return parseAmountInput(text);
}

/** Centavos for a draft; null when empty or zero. */
export function draftToMinor(draft: string): number | null {
  if (draft === '') return null;
  const [whole = '', decimals = ''] = draft.split('.');
  const minor = Number(whole || '0') * 100 + Number(`${decimals}00`.slice(0, 2));
  return Number.isFinite(minor) && minor > 0 ? minor : null;
}

export function minorToDraft(minor: number): string {
  const whole = Math.floor(minor / 100);
  const cents = minor % 100;
  return cents === 0 ? String(whole) : `${whole}.${String(cents).padStart(2, '0')}`;
}

/** The draft as the input shows it while typing: grouped whole part, decimals as typed. */
export function formatAmountInput(draft: string): string {
  if (draft === '') return '';
  const dot = draft.indexOf('.');
  const whole = dot >= 0 ? draft.slice(0, dot) : draft;
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${grouped}${dot >= 0 ? draft.slice(dot) : ''}`;
}

/** On blur: '12.5' becomes '12.50', '12.' becomes '12', and a zero amount clears. */
export function settleDraft(draft: string): string {
  const minor = draftToMinor(draft);
  return minor === null ? '' : minorToDraft(minor);
}

/** How many significant characters (digits and '.') come before `caret` in `text`. */
export function significantBefore(text: string, caret: number): number {
  let n = 0;
  for (const ch of text.slice(0, caret)) if (isSignificant(ch)) n += 1;
  return n;
}

/** The caret position in `formatted` just after its `count`-th significant character. */
export function caretAfterSignificant(formatted: string, count: number): number {
  if (count <= 0) return 0;
  let n = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (isSignificant(formatted[i]!)) n += 1;
    if (n === count) return i + 1;
  }
  return formatted.length;
}
